import { useCallback, useMemo } from "react";
import ContractDetails from "../../fragments/contract-details";
import { useGetInsuranceQuery, useGetLocationQuery, useGetPriceOverwritesQuery, useGetRelativesQuery } from "../../../../api/plamo-api";
import dayjs from "dayjs";

function CustomerContractDetails({ customerId, customer, contractId }) {
  const { data: insurance } = useGetInsuranceQuery({ id: customer?.insuranceId }, { skip: !customer?.insuranceId }) as any;
  const { data: relatives } = useGetRelativesQuery({ f: JSON.stringify({ "customer.id": { c: "EQ", v: customerId } }) }) as any;
  const { data: location } = useGetLocationQuery({ id: customer?.locationId }, { skip: !customer?.locationId }) as any;

  const filter = useMemo(() => ({ customer: { c: "EQ", v: null }}), [])
  const { data: pricesDefault } = useGetPriceOverwritesQuery({ f: JSON.stringify(filter) }) as any;

  const filterOverwrites = useMemo(() => customerId ? { "customer.id": { c: "EQ", v: customerId } } : { customer: { c: "EQ", v: null }}, [customerId])
  const { data: priceOverwrites } = useGetPriceOverwritesQuery({ f: JSON.stringify(filterOverwrites) }) as any;

  const fillContract = useCallback((contractTemplate, lastContractDate, effectiveDate, validUntil) => {
    if(contractTemplate && customer) {
      const legalRepresentative = relatives?.items?.find(r => r.legalRepresentative);

      const legalRepresentativeReplacement = legalRepresentative ? `
        <p class="ql-align-center">&nbsp;</p><p class="ql-align-center">Vertreten durch (als Bevollmächtigte/r oder rechtliche/r Betreuer/in):</p><p class="ql-align-center">${legalRepresentative?.firstName} ${legalRepresentative?.lastName}</p>
      ` : "";

      const missingFields: { name: string, hint: string | undefined }[] = []

      const checkMissing = (v, fieldName, hint = undefined) => {
        if(!v) {
          missingFields.push({ name: fieldName, hint });
          return "<span style=\"font-weight: bold; background-color: red;\">Wert fehlt!</span>"
        }
        return v;
      }

      console.log("priceOverwrites", priceOverwrites, priceOverwrites?.items?.find(po => po.ident === 'supportAmountCalloutFeeByHour')?.value)

      const fill = (template: string) => template
        .replace(/<h2>/g, '<h2 data-break-after="avoid">')
        .replace(/{ANREDE}/g, () => checkMissing(customer.salutation ? (customer.salutation === 'M' ? 'Lieber Herr' : 'Liebe Frau') : null, "Anrede"))
        .replace(/{VORNAME}/g, () => checkMissing(customer.firstName, "Vorname"))
        .replace(/{NACHNAME}/g, () => checkMissing(customer.lastName, "Nachname"))
        .replace(/{STRASSE}/g, () => checkMissing(customer.addressStreet, "Straße"))
        .replace(/{HAUSNUMMER}/g, () => checkMissing(customer.addressHouseNumber, "Hausnummer"))
        .replace(/{PLZ}/g, () => checkMissing(customer.addressZipCode, "PLZ"))
        .replace(/{ORT}/g, () => checkMissing(customer.addressCity, "Ort"))
        .replace(/{PREIS_ELB}/g, () => checkMissing(priceOverwrites?.items?.find(po => po.ident === 'supportAmountHourlyRate')?.value?.toFixed(2).replace('.', ',') || pricesDefault?.items?.find(po => po.ident === 'supportAmountHourlyRate')?.value?.toFixed(2).replace('.', ','), "Preis ELB"))
        .replace(/{PREIS_ELB_HAUSBESUCHSPAUSCHALE}/g, () => checkMissing((priceOverwrites?.items?.find(po => po.ident === 'supportAmountCalloutFeeByHour')?.value || undefined)?.toFixed(2).replace('.', ',') || (pricesDefault?.items?.find(po => po.ident === 'supportAmountCalloutFeeFixed')?.value || undefined)?.toFixed(2).replace('.', ','), "Preis ELB Hausbesuchspauschale"))
        .replace(/{PREIS_VHP}/g, () => checkMissing(priceOverwrites?.items?.find(po => po.ident === 'absenceCareHourlyRate')?.value?.toFixed(2).replace('.', ',') || pricesDefault?.items?.find(po => po.ident === 'absenceCareHourlyRate')?.value?.toFixed(2).replace('.', ','), "Preis VHP"))
        .replace(/{PREIS_VHP_HAUSBESUCHSPAUSCHALE}/g, () => checkMissing((priceOverwrites?.items?.find(po => po.ident === 'absenceCareCalloutFeeHourlyRate')?.value || undefined)?.toFixed(2).replace('.', ',') || (pricesDefault?.items?.find(po => po.ident === 'absenceCareCalloutFeeFixed')?.value || undefined)?.toFixed(2).replace('.', ','), "Preis VHP Hausbesuchspauschale"))
        .replace(/{PREIS_ELB_HAUSBESUCHSPAUSCHALE_VIERTELSTUNDE}/g, () => checkMissing(((priceOverwrites?.items?.find(po => po.ident === 'supportAmountCalloutFeeByHour')?.value / 4) || undefined)?.toFixed(2).replace('.', ',') || ((pricesDefault?.items?.find(po => po.ident === 'supportAmountCalloutFeeByHour')?.value / 4) || undefined)?.toFixed(2).replace('.', ','), "Preis ELB Anfahrt pro Stunde"))
        .replace(/{KRANKENKASSE}/g, () => checkMissing(insurance?.name, "Versicherung"))
        .replace(/{VERSICHERTENNUMMER}/g, () => checkMissing(customer.socialSecurityNumber, "Versichertennummer"))
        .replace(/{VERTRAGSBEGINN}/g, () => checkMissing(effectiveDate ? dayjs(effectiveDate).format('DD.MM.YYYY') : null, "Vertragsbeginn"))
        .replace(/{GESETZLICHER_VERTRETER}/g, () => legalRepresentativeReplacement || "")
        .replace(/{RUECKSENDEADRESSE}/g, () => checkMissing(location?.address ? location.address.replace(/\n/g, '<br/>') : null, "Rücksendeadresse"))
        .replace(/{RUECKSENDEEMAIL}/g, () => checkMissing(location?.emailAddress, "Rücksende-E-Mail-Adresse"))

      const filledContract = fill(contractTemplate.text || "");
      const filledLetter = fill(contractTemplate.letterText || "")

      return { filledContract, filledLetter, missingFields };
    }
    return { filledContract: '', filledLetter: '', missingFields: [] };
  }, [customer, priceOverwrites, pricesDefault, insurance, relatives, location])

  return (<ContractDetails 
    contractId={contractId} 
    contractObjectType="customer" 
    contractObjectId={customer?.id} 
    fillContract={fillContract} 
    signerFirstName={customer?.firstName} 
    signerLastName={customer?.lastName} 
    signerRole="Leistungsnehmer/in" 
    navigationBasePath={`/customer/${customer?.id}/contracts`} 
    locationId={customer?.locationId}
    address={{ firstName: customer?.firstName, 
      lastName: customer?.lastName, 
      street: customer?.addressStreet, 
      houseNumber: customer?.addressHouseNumber, 
      zipCode: customer?.addressZipCode, 
      city: customer?.addressCity 
    }}
     />)
}

export default CustomerContractDetails;